/**
 * Task: ArticleViewer — renders the selected article's markdown content.
 *
 * Uses react-markdown + remark-gfm for tables / strikethrough / task-lists.
 */
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Loader2, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ArticleViewer({ slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) {
      setArticle(null);
      return;
    }
    setLoading(true);
    fetch(`${BACKEND_URL}/api/documentation/articles/${slug}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setArticle(data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!slug) {
    return (
      <Card className="p-16 flex flex-col items-center justify-center text-center text-slate-500" data-testid="article-viewer-empty">
        <BookOpen className="h-12 w-12 text-slate-300 mb-3" />
        <p className="text-base font-medium text-slate-600">Pick an article from the list</p>
        <p className="text-sm text-slate-400 mt-1">Or search above to find something specific.</p>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="p-12 flex items-center justify-center text-slate-500" data-testid="article-viewer-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading article…
      </Card>
    );
  }

  if (!article) {
    return (
      <Card className="p-8 text-center text-slate-500" data-testid="article-viewer-error">
        Couldn&apos;t load article.
      </Card>
    );
  }

  return (
    <Card className="p-8 max-h-[78vh] overflow-y-auto" data-testid={`article-viewer-${article.slug}`}>
      {/* Meta header */}
      <div className="flex items-center gap-2 flex-wrap mb-4 pb-4 border-b border-slate-200">
        <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
          {article.category}
        </span>
        {(article.tags || []).slice(0, 5).map((t) => (
          <span key={t} className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
            #{t}
          </span>
        ))}
      </div>

      {/* Markdown content */}
      <article className="prose prose-slate max-w-none prose-headings:font-bold prose-h1:text-2xl prose-h1:mb-3 prose-h1:mt-0 prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2 prose-h2:text-blue-900 prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1.5 prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm prose-li:my-0.5 prose-code:text-rose-700 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:text-xs prose-table:text-xs prose-th:bg-slate-100 prose-th:text-slate-700 prose-th:font-bold prose-td:border prose-td:border-slate-200 prose-td:px-2 prose-td:py-1.5 prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:text-sm prose-blockquote:text-blue-900 prose-strong:text-slate-900 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
      </article>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-100 text-[11px] text-slate-400">
        Updated: {article.updated_at?.slice(0, 10) || '—'} · Slug: <code>{article.slug}</code>
      </div>
    </Card>
  );
}
